import { AUTH_TOKEN } from '../constants/index';

// Token management
export const TokenManager = {
    // Get token from storage
    getToken: () => {
        return sessionStorage.getItem(AUTH_TOKEN) || localStorage.getItem(AUTH_TOKEN);
    },

    // Set token in storage
    setToken: (token, rememberMe = false) => {
        if (rememberMe) {
            localStorage.setItem(AUTH_TOKEN, token);
        } else {
            sessionStorage.setItem(AUTH_TOKEN, token);
        }
    },

    // Remove token from storage
    removeToken: () => {
        sessionStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(AUTH_TOKEN);
    },

    // Check if token exists
    hasToken: () => {
        return !!(sessionStorage.getItem(AUTH_TOKEN) || localStorage.getItem(AUTH_TOKEN));
    },

    // Validate token format (basic validation)
    isValidToken: token => {
        if (!token) return false;

        // Basic JWT format validation
        const parts = token.split('.');
        if (parts.length !== 3) return false;

        try {
            // Check if payload is valid JSON
            const payload = JSON.parse(atob(parts[1]));

            // Check if token is expired
            if (payload.exp && payload.exp < Date.now() / 1000) {
                return false;
            }

            return true;
        } catch (error) {
            return false;
        }
    },
};

// Authentication utilities
export const AuthUtils = {
    // Check if user is authenticated
    isAuthenticated: () => {
        const token = TokenManager.getToken();
        return TokenManager.isValidToken(token);
    },

    // Get user info from token
    getUserFromToken: () => {
        const token = TokenManager.getToken();
        if (!token) return null;

        try {
            const parts = token.split('.');
            const payload = JSON.parse(atob(parts[1]));
            return payload;
        } catch (error) {
            console.error('Error parsing token:', error);
            return null;
        }
    },

    // Logout user
    logout: () => {
        TokenManager.removeToken();
        // Clear any other stored data
        sessionStorage.clear();
        localStorage.clear();

        // Redirect to login or refresh page
        window.location.reload();
    },

    // Handle authentication error
    handleAuthError: error => {
        console.warn('Authentication error:', error);

        // Clear invalid token
        TokenManager.removeToken();

        // Show user-friendly message
        const message = error?.message?.includes('UNAUTHENTICATED')
            ? 'Your session has expired. Please log in again.'
            : 'Authentication failed. Please try again.';

        return message;
    },
};

// Security utilities
export const SecurityUtils = {
    // Sanitize input to prevent XSS
    sanitizeInput: input => {
        if (typeof input !== 'string') return input;

        return input
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;')
            .replace(/\//g, '&#x2F;');
    },

    // Validate email format
    isValidEmail: email => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    },

    // Validate password strength
    validatePassword: password => {
        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        const errors = [];

        if (password.length < minLength) {
            errors.push(`Password must be at least ${minLength} characters long`);
        }
        if (!hasUpperCase) {
            errors.push('Password must contain at least one uppercase letter');
        }
        if (!hasLowerCase) {
            errors.push('Password must contain at least one lowercase letter');
        }
        if (!hasNumbers) {
            errors.push('Password must contain at least one number');
        }
        if (!hasSpecialChar) {
            errors.push('Password must contain at least one special character');
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    },

    // Generate secure random string
    generateSecureToken: (length = 32) => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        const randomArray = new Uint8Array(length);
        crypto.getRandomValues(randomArray);

        for (let i = 0; i < length; i++) {
            result += chars.charAt(randomArray[i] % chars.length);
        }

        return result;
    },

    // Hash string (basic implementation)
    hashString: async str => {
        const encoder = new TextEncoder();
        const data = encoder.encode(str);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    },
};

// Rate limiting utilities
export const RateLimitUtils = {
    requests: new Map(),

    // Check if request should be rate limited
    isRateLimited: (key, maxRequests = 5, windowMs = 60000) => {
        const now = Date.now();
        const windowStart = now - windowMs;

        if (!RateLimitUtils.requests.has(key)) {
            RateLimitUtils.requests.set(key, []);
        }

        const requests = RateLimitUtils.requests.get(key);

        // Remove old requests outside the window
        const validRequests = requests.filter(timestamp => timestamp > windowStart);
        RateLimitUtils.requests.set(key, validRequests);

        // Check if limit exceeded
        if (validRequests.length >= maxRequests) {
            return true;
        }

        // Add current request
        validRequests.push(now);
        return false;
    },

    // Clear rate limit for a key
    clearRateLimit: key => {
        RateLimitUtils.requests.delete(key);
    },
};

// CSRF protection
export const CSRFUtils = {
    // Generate CSRF token
    generateCSRFToken: () => {
        return SecurityUtils.generateSecureToken(32);
    },

    // Validate CSRF token
    validateCSRFToken: (token, storedToken) => {
        return token === storedToken;
    },
};

// Export default security utilities
export default {
    TokenManager,
    AuthUtils,
    SecurityUtils,
    RateLimitUtils,
    CSRFUtils,
};
